/**
 * Helper: Convert ArrayBuffer to hex string
 */
const bufferToHex = buffer => {
  const view = new Uint8Array(buffer);
  return Array.from(view)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Compute SHA-256 hash of a file using Web Crypto API
 * Strategy: First hash chunk (1MB), then full file if needed
 * Web Crypto API is built-in and very fast
 * @param {File} file - File to hash
 * @param {number} chunkSize - Size of initial chunk in bytes (default 1MB)
 * @returns {Promise<{fullHash: string, chunkHash: string, fileSize: number}>}
 */
export const computeFileHash = async (file, chunkSize = 1024 * 1024) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // First: Compute chunk hash
    reader.onload = async e => {
      try {
        const chunkData = e.target.result;
        const chunkHashBuffer = await crypto.subtle.digest('SHA-256', chunkData);
        const chunkHash = bufferToHex(chunkHashBuffer);

        // If file is small, chunk hash is enough
        if (file.size <= chunkSize) {
          resolve({
            fullHash: chunkHash,
            chunkHash,
            fileSize: file.size,
            lastModified: file.lastModified
          });
          return;
        }

        // File is larger, compute full hash
        const fullReader = new FileReader();
        fullReader.onload = async fullEvent => {
          try {
            const fullData = fullEvent.target.result;
            const fullHashBuffer = await crypto.subtle.digest('SHA-256', fullData);
            const fullHash = bufferToHex(fullHashBuffer);
            resolve({
              fullHash,
              chunkHash,
              fileSize: file.size,
              lastModified: file.lastModified
            });
          } catch (err) {
            reject(err);
          }
        };
        fullReader.onerror = reject;
        fullReader.readAsArrayBuffer(file);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = reject;

    // Read first chunk
    const blob = file.slice(0, chunkSize);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Detect duplicate files using metadata + hash comparison
 * Production-grade detection: metadata first, then hash
 * Handles cases where existing files have limited metadata from server
 * @param {File[]} newFiles - Files being uploaded
 * @param {Object[]} existingFiles - Existing files with structure: { name, size?, hash?, lastModified?, ... }
 * @returns {Promise<Array>} Array of duplicates with detection reason
 */
export const detectDuplicateFiles = async (newFiles, existingFiles) => {
  const duplicates = [];

  if (!existingFiles || existingFiles.length === 0) return duplicates;

  for (const newFile of newFiles) {
    // Step 1: Quick metadata check - name match (primary indicator)
    // Size check is optional since backend may not provide it
    const metadataMatches = existingFiles.filter(existing => {
      const nameMatches = existing?.name?.toLowerCase() === newFile.name.toLowerCase();

      // If size info exists on both, require size match too
      if (nameMatches && existing?.size !== undefined) {
        return existing.size === newFile.size;
      }

      // If size info missing on server file, just match by name
      return nameMatches;
    });

    if (metadataMatches.length === 0) continue;

    // Step 2: Compute hash for new file to verify
    let newFileHash;
    try {
      newFileHash = await computeFileHash(newFile);
    } catch (err) {
      console.error(`Failed to compute hash for ${newFile.name}:`, err);
      // If hash fails, treat as duplicate based on metadata
      duplicates.push({
        fileName: newFile.name,
        fileSize: newFile.size,
        newFile,
        existingFile: metadataMatches[0],
        reason: 'metadata',
        details: {
          hasHashMismatch: true,
          message: 'File with this name already exists'
        }
      });
      continue;
    }

    // Step 3: Compare hash with existing files if hash info available
    // If existing files don't have hash (from server), rely on name + optional size
    let hashMatch = null;
    const hasServerHash = metadataMatches.some(f => f.hash || f.chunkHash);

    if (hasServerHash) {
      // Server provided hash info, do full comparison
      hashMatch = metadataMatches.find(
        existing =>
          existing?.hash === newFileHash.fullHash || existing?.chunkHash === newFileHash.chunkHash
      );
    } else {
      // Server didn't provide hash, use first name match
      hashMatch = metadataMatches[0];
    }

    if (hashMatch) {
      duplicates.push({
        fileName: newFile.name,
        fileSize: newFile.size,
        newFile,
        existingFile: hashMatch,
        reason: hasServerHash ? 'hash' : 'metadata',
        newFileHash,
        details: {
          hasHashMismatch: false,
          message: hasServerHash
            ? 'This file already exists (identical content)'
            : 'A file with this name already exists',
          hashMatch: hasServerHash
            ? hashMatch.hash === newFileHash.fullHash ||
              hashMatch.chunkHash === newFileHash.chunkHash
            : null
        }
      });
    }
  }

  return duplicates;
};

/**
 * Build a stable identity key for a file or duplicate object.
 * Works with both File objects (name/size/lastModified) and duplicate
 * payload objects (fileName/fileSize/lastModified).
 * @param {File|Object} file
 * @returns {string}
 */
export const getFileIdentityKey = file =>
  `${file?.fileName ?? file?.name}::${file?.fileSize ?? file?.size}::${file?.lastModified}`;

/**
 * Format file size for display
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format date for display
 * @param {number} timestamp
 * @returns {string}
 */
export const formatDate = timestamp => {
  if (!timestamp) return 'Unknown';
  return new Date(timestamp).toLocaleDateString() + ' ' + new Date(timestamp).toLocaleTimeString();
};
